import React from 'react'
import { Link, usePage, Head } from '@inertiajs/react'
import {
  Building2,
  CreditCard,
  ArrowRight,
  ReceiptText,
  CalendarClock,
  BadgePercent,
  AlertCircle,
} from 'lucide-react'
import ClaimShell from '@/components/claim/ClaimShell'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useBrand } from '@/hooks/useBrand'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { formatMoney } from '@/lib/format'
import BillingRoleNote from '@/components/billing/BillingRoleNote'

interface QuoteRow {
  id: number
  name: string
  currency: string
  /** Server-resolved by role: null for a combined inventory's following listing (see Billing.tsx). */
  roomCount: number | null
  monthlyAmount: number | null
  /** The listing's combined-inventory role, server-composed; null outside a combined inventory. */
  billingLabel?: string | null
}

interface AgreementSummary {
  billingInterval: string
  discountPct: string | number
  setupFeeAmount: number | null
  termMonths: number | null
  // The ONE account-level plan name (the catalog plan this agreement snapshots) —
  // shown once on the agreement card, never per property (plan-model refactor §7.4).
  planName: string | null
}

interface ReviewProps {
  currentUser: { id: number; email: string }
  account: { id: number; name: string; billingEmail: string | null } | null
  agreement: AgreementSummary | null
  properties: QuoteRow[]
}

// How the per-property amount recurs, in human terms. The backend amount is ALWAYS the
// monthly figure (PriceStructure#price_for); this only labels the cadence the agreement
// bills on — never math on the amount itself.
const INTERVAL_LABEL: Record<string, string> = {
  month: 'Billed monthly',
  quarter: 'Billed quarterly',
  semiannual: 'Billed every 6 months',
  year: 'Billed annually',
}

function intervalLabel(interval: string): string {
  return INTERVAL_LABEL[interval] ?? 'Billed monthly'
}

export default function Review({ currentUser, account, agreement, properties }: ReviewProps) {
  // A failed card-capture step (claims#payment_capture) redirects back here with a flash alert;
  // surface it (display only).
  const { props } = usePage<{ flash?: { alert?: string | null; notice?: string | null } }>()
  const flashAlert = props.flash?.alert
  const brand = useBrand()

  return (
    <ClaimShell currentStep={2} footerNote={`Signed in as ${currentUser.email}`}>
      <Head title="Review your order" />
      <div className="mx-auto max-w-2xl space-y-6">
        {flashAlert && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 animate-fade-in-up">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{flashAlert}</span>
          </div>
        )}

        {/* Heading */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Review your order</h1>
            <HelpTooltip content={`These are the terms negotiated for your account. Each property is priced by its room count on your plan. Amounts are calculated by ${brand} and shown exactly as they bill.`} />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Confirm the plan below, then continue to secure payment.
          </p>
        </section>

        {/* Account card */}
        <section className="animate-slide-in" style={{ animationDelay: '50ms' }}>
          <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {account?.name ?? 'Your account'}
                </p>
                {account?.billingEmail && (
                  <p className="truncate text-xs text-muted-foreground">{account.billingEmail}</p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Agreement terms — reads like the header of an order form */}
        {agreement && (
          <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 px-5 py-4">
                <div className="flex items-center gap-2">
                  <ReceiptText className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-semibold text-foreground">Agreement terms</span>
                </div>
                {/* The plan appears ONCE, at agreement level — it governs every property below. */}
                {agreement.planName && (
                  <Badge variant="outline" className="border-primary/30 bg-primary/5 text-[11px] capitalize text-primary">
                    {agreement.planName} plan
                  </Badge>
                )}
              </div>
              <dl className="grid grid-cols-1 divide-y divide-border/40 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <TermTile
                  icon={<CalendarClock className="h-4 w-4 text-muted-foreground" />}
                  label="Billing cadence"
                  value={intervalLabel(agreement.billingInterval).replace('Billed ', '')}
                />
                <TermTile
                  icon={<BadgePercent className="h-4 w-4 text-muted-foreground" />}
                  label="Discount"
                  value={`${agreement.discountPct}%`}
                />
                <TermTile
                  icon={<CalendarClock className="h-4 w-4 text-muted-foreground" />}
                  label="Term"
                  value={agreement.termMonths ? `${agreement.termMonths} months` : 'No minimum'}
                />
              </dl>
              {agreement.setupFeeAmount !== null && agreement.setupFeeAmount > 0 && (
                <div className="border-t border-border/40 bg-muted/30 px-5 py-3 text-xs text-muted-foreground">
                  A one-time setup fee of{' '}
                  <span className="font-semibold tabular-nums text-foreground">
                    {formatMoney(agreement.setupFeeAmount, properties[0]?.currency ?? 'GBP')}
                  </span>{' '}
                  applies at checkout.
                </div>
              )}
            </div>
          </section>
        )}

        {/* Per-property quote — the order's line items */}
        <section className="animate-slide-in" style={{ animationDelay: '150ms' }}>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Properties on this plan</h2>
            <HelpTooltip content={`Each property is billed by its room count on your plan's rates. The monthly amount is calculated by ${brand}.`} />
          </div>

          {properties.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Building2 className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No properties yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Your properties will appear here once they&apos;re connected. You can still
                continue to set up billing.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead className="text-right">Rooms</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium text-foreground">
                        {p.name}
                        <BillingRoleNote label={p.billingLabel} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                        {p.roomCount ?? 'Add-on'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-foreground">
                        {p.monthlyAmount === null ? '—' : `${formatMoney(p.monthlyAmount, p.currency)}/mo`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Amounts are computed by {brand} and shown exactly as billed. Taxes are added at checkout.
          </p>
        </section>

        {/* Action — FE3: navigate to the in-app card-capture step (GET /claim/payment renders
            the PaymentElement). Page navigation, so an Inertia <Link> (renders <a>), never a
            router.post — no hosted-Checkout redirect anymore. */}
        <section className="animate-slide-in pb-8" style={{ animationDelay: '200ms' }}>
          <Link
            href="/claim/payment"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            <CreditCard className="h-4 w-4" />
            Continue to payment
            <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            You&apos;ll add your card securely on the next step, without leaving this page.
          </p>
        </section>
      </div>
    </ClaimShell>
  )
}

function TermTile({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-base font-semibold capitalize tabular-nums text-foreground">{value}</div>
    </div>
  )
}
